import React, { Component } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { ArrowBack, ArrowForward, Close, Delete, Edit } from '@mui/icons-material';
import { AppBar, Box, Button, Dialog, FormControl, IconButton, Paper, Slide, Toolbar, Typography } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

import { isSystemTemplate, isUserTemplate } from '@paragon/client-tools';
import { hasOrganizationTemplatesPermission } from '@paragon/login-tools';
import { enqueueWarning, NotifierConfirm } from '@paragon/notification-tools';

import { renameTemplate, saveModifiedTemplates } from '../../actions/annotationTemplates';
import { getSortedAnnotationTemplates, getSortedTemplateGroupNames } from '../../selectors/annotationTemplates';
import { RootState } from '../../store';
import {
  assignTemplateToGroup,
  createTemplateGroup,
  deleteAnnotationTemplate,
  deleteTemplateGroup,
  renameTemplateGroup,
} from '../../store/annotationTemplates';
import { GroupList, TemplateList } from './TemplateDirectory';

const groupEditButtonsSx = {
  m: 1,
  py: 1,
  width: '100%',
  bgcolor: 'backContrast.main',
  '&:hover': {
    bgcolor: 'primary.dark',
    color: '#fff',
  },
} as const;

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;

type OwnState = {
  group: string;
  templateName: string;
  managerOpen: boolean;
  deleteGroupOpen: boolean;
  deleteTemplateOpen: boolean;
  unassignTemplateOpen: boolean;
  lastTemplateIdInGroup: number | null;
};

// https://material-ui.com/components/dialogs/#full-screen-dialogs
const Transition = React.forwardRef(
  (props: TransitionProps & { children: React.ReactElement }, ref: React.Ref<unknown>) => (
    <Slide direction="up" ref={ref} {...props} />
  ),
);

export class UnconnectedTemplateManager extends Component<Props, OwnState> {
  state: OwnState = {
    group: '',
    templateName: '',
    managerOpen: false,
    deleteGroupOpen: false,
    deleteTemplateOpen: false,
    unassignTemplateOpen: false,
    lastTemplateIdInGroup: null,
  };

  onSelectGroup = (newGroup: string | null) => {
    this.setState({ group: newGroup || '' });
  };

  get hasNoSelectedGroup() {
    return this.state.group == null || this.state.group === '';
  }

  handleClickOpen = () => {
    this.setState({ managerOpen: true });
  };

  handleClose = () => {
    this.props.saveModifiedTemplates();
    this.setState({ managerOpen: false });
  };

  onRenameTemplate = (templateId: number) => {
    const { name } = this.findTemplate(templateId);

    const newName = prompt(`Please enter a new name for template "${name}"`);
    if (!newName || newName === '') {
      console.log('Failed to rename template. Name was left blank.');
      return;
    }

    this.props.renameTemplate(templateId, newName);
  };

  onDeleteTemplate = (templateId: number) => {
    const { name } = this.findTemplate(templateId);

    this.setState({
      lastTemplateIdInGroup: templateId,
      deleteTemplateOpen: true,
      templateName: name,
    });
  };

  confirmDeleteTemplate = () => {
    this.props.deleteAnnotationTemplate(this.state.lastTemplateIdInGroup!);
    this.closeDeleteTemplate();
  };

  closeDeleteTemplate = () => {
    this.setState({
      deleteTemplateOpen: false,
      lastTemplateIdInGroup: null,
      templateName: '',
    });
  };

  onAssignTemplate = (templateId: number) => {
    this.props.assignTemplateToGroup({ templateId, groupName: this.state.group });
  };

  onUnassignTemplate = (templateId: number) => {
    const templatesInGroup = this.props.templates.filter((template) => template.group === this.state.group);
    if (templatesInGroup.length === 1) {
      this.setState({
        unassignTemplateOpen: true,
        lastTemplateIdInGroup: templateId,
      });
    } else {
      this.props.assignTemplateToGroup({ templateId, groupName: null });
    }
  };

  confirmUnassignTemplate = () => {
    this.props.assignTemplateToGroup({ templateId: this.state.lastTemplateIdInGroup!, groupName: null });
    this.setState({ group: '' });
    this.closeUnassignTemplate();
  };

  closeUnassignTemplate = () => {
    this.setState({
      unassignTemplateOpen: false,
      lastTemplateIdInGroup: null,
    });
  };

  onRenameGroup = () => {
    const oldName = this.state.group;

    const newName = prompt(`Please enter a new name for group "${oldName}"`);
    if (!newName || newName === '') {
      console.log('Failed to rename group. Name was left blank.');
      return;
    }

    this.props.renameTemplateGroup({ oldName, newName });
    this.setState({ group: newName });
  };

  onDeleteGroup = () => {
    this.setState({ deleteGroupOpen: true });
  };

  confirmDeleteGroup = () => {
    const { groups } = this.props;
    const groupName = this.state.group;

    if (this.state.group === groupName) {
      if (groups.length > 1) {
        const nextGroupIndex = (groups.indexOf(groupName) + 1) % groups.length;

        this.setState({ group: groups[nextGroupIndex] });
      } else {
        this.setState({ group: '' });
      }
    }

    this.props.deleteTemplateGroup(this.state.group);
    this.setState({ deleteGroupOpen: false });
  };

  closeDeleteGroup = () => {
    this.setState({ deleteGroupOpen: false });
  };

  onCreateGroup = () => {
    const group = prompt('Please enter a name for the new group.');
    if (group == null) {
      return;
    }

    this.props.createTemplateGroup(group);
    this.setState({ group });
  };

  findTemplate(templateId: number) {
    return this.props.templates.find((template) => template.id === templateId);
  }

  render() {
    return (
      <div>
        <Button
          variant="outlined"
          onClick={this.handleClickOpen}
          sx={{
            mb: 1,
            py: 1,
            width: '100%',
            bgcolor: 'backContrast.main',
            '&:hover': {
              bgcolor: 'primary.dark',
              color: '#fff',
            },
          }}
        >
          Template Manager
        </Button>
        <Dialog fullScreen open={this.state.managerOpen} onClose={this.handleClose} slots={{ transition: Transition }}>
          <AppBar
            sx={{
              position: 'relative',
              borderBottom: 'solid 3px #f4d166',
              color: '#fff',
            }}
          >
            <Toolbar disableGutters>
              <Typography variant="h6" sx={{ color: 'inherit', flex: 1, pl: 3 }}>
                Template Manager
              </Typography>
              <IconButton
                color="inherit"
                onClick={this.handleClose}
                aria-label="Close"
                sx={{ height: 64, width: 64, fontSize: 28, bgcolor: 'paragon.main', borderRadius: 0 }}
              >
                <Close sx={{ fontSize: 28 }} />
              </IconButton>
            </Toolbar>
          </AppBar>
          <Box
            sx={{ display: 'flex', flexGrow: 1, justifyContent: 'space-evenly', pt: 3, bgcolor: 'background.default' }}
          >
            <Box sx={{ width: '40%' }}>
              <Typography>All Templates</Typography>
              <TemplateList
                sx={{
                  mt: 1,
                  maxHeight: '70vh',
                  overflowY: 'hidden',
                  '&:hover': {
                    overflowY: 'auto',
                  },
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  scrollbarWidth: 'none',
                  bgcolor: 'backContrast.main',
                }}
                showGroups
                templates={this.props.templates}
                selectedTemplateId={this.props.selectedTemplate ? this.props.selectedTemplate.id : undefined}
                secondaryActions={[
                  {
                    key: 'edit',
                    icon: <Edit />,
                    tooltip: 'Rename',
                    onClick: this.onRenameTemplate,
                  },
                  {
                    key: 'delete',
                    icon: <Delete />,
                    tooltip: 'Delete',
                    onClick: this.onDeleteTemplate,
                  },
                  {
                    key: 'assign-to-group',
                    icon: <ArrowForward />,
                    tooltip: 'Assign to group',
                    onClick: this.onAssignTemplate,
                    disabled: this.hasNoSelectedGroup,
                  },
                ]}
              />
            </Box>
            <Box sx={{ width: '40%' }}>
              <Typography>All Groups</Typography>
              <Paper sx={{ bgcolor: 'backContrast.main', py: 1, mt: 1, borderRadius: 0 }}>
                <FormControl
                  sx={{
                    py: 1,
                    width: '100%',
                    maxHeight: 200,
                    overflowY: 'hidden',
                    '&:hover': {
                      overflowY: 'auto',
                    },
                    '&::scrollbar': {
                      display: 'none',
                    },
                  }}
                >
                  <GroupList
                    sx={{
                      overflowY: 'hidden',
                      '&:hover': {
                        overflowY: 'auto',
                      },
                      '&::-webkit-scrollbar': {
                        display: 'none',
                      },
                      scrollbarWidth: 'none',
                    }}
                    selectedGroup={this.state.group}
                    onSelectGroup={this.onSelectGroup}
                  />
                </FormControl>
              </Paper>
              <Box sx={{ my: 1, display: 'flex' }}>
                <Button
                  size="small"
                  variant="outlined"
                  sx={groupEditButtonsSx}
                  onClick={this.onRenameGroup}
                  disabled={this.hasNoSelectedGroup}
                >
                  Rename Group
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  sx={groupEditButtonsSx}
                  onClick={this.onDeleteGroup}
                  disabled={this.hasNoSelectedGroup}
                >
                  Delete Group
                </Button>
                <Button size="small" variant="outlined" sx={groupEditButtonsSx} onClick={this.onCreateGroup}>
                  Create Group
                </Button>
              </Box>
              <div>
                <Typography gutterBottom>Selected Group&apos;s Templates</Typography>
              </div>

              <TemplateList
                sx={{
                  mt: 1,
                  maxHeight: 300,
                  overflowY: 'hidden',
                  '&:hover': {
                    overflowY: 'auto',
                  },
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  scrollbarWidth: 'none',
                  bgcolor: 'backContrast.main',
                }}
                templates={this.props.templates.filter((template) => template.group === this.state.group)}
                secondaryActions={[
                  {
                    key: 'remove-from-group',
                    alignLeft: true,
                    icon: <ArrowBack />,
                    tooltip: 'Remove from group',
                    onClick: this.onUnassignTemplate,
                  },
                ]}
                placeholder={
                  <Paper sx={{ p: 1 }}>
                    <Typography>Please select a group to view its templates</Typography>
                  </Paper>
                }
              />
            </Box>
          </Box>
          <NotifierConfirm
            open={this.state.deleteGroupOpen}
            onClose={this.closeDeleteGroup}
            onClick={this.confirmDeleteGroup}
            message={`Are you sure you want to delete group "${this.state.group}"? All templates assigned to this group will be unassigned.`}
            buttonText="Delete"
          />
          <NotifierConfirm
            open={this.state.unassignTemplateOpen}
            onClose={this.closeUnassignTemplate}
            onClick={this.confirmUnassignTemplate}
            message="Removing the last template from a group will cause that group to be deleted. Continue?"
            buttonText="Remove"
          />
          <NotifierConfirm
            open={this.state.deleteTemplateOpen}
            onClose={this.closeDeleteTemplate}
            onClick={this.confirmDeleteTemplate}
            message={`Are you sure you want to delete template "${this.state.templateName}"`}
            buttonText="Delete"
          />
        </Dialog>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  templates: getSortedAnnotationTemplates(state).filter((template) =>
    !isSystemTemplate(template) && hasOrganizationTemplatesPermission(state) ? true : isUserTemplate(template),
  ),
  selectedTemplate:
    state.annotationTemplates.selectedTemplate &&
    state.annotationTemplates.annotationTemplates[state.annotationTemplates.selectedTemplate],

  groups: getSortedTemplateGroupNames(state),
});

const actionCreators = {
  assignTemplateToGroup,
  renameTemplate,
  deleteAnnotationTemplate,
  createTemplateGroup,
  renameTemplateGroup,
  deleteTemplateGroup,
  saveModifiedTemplates,
  enqueueWarning,
};

export default connect(mapStateToProps, actionCreators)(UnconnectedTemplateManager);
